import { fetcher, fetcherConfig } from '../../../lib/api-fetcher';
import { createResourceClient, createResourceHooks } from '@repo/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  InstructionListItemSchema,
  InstructionDetailSchema,
  ImportListItemSchema,
  ImportDetailSchema,
  type InstructionListItem,
  type InstructionDetail,
  type ImportListItem,
  type ImportDetail,
  type UnmatchedRow,
  type ReconciliationCorrectionPayload,
} from '../types.js';



// --- Instructions ---

export const instructionClient = createResourceClient<InstructionListItem, InstructionDetail, any, any>(
  fetcherConfig,
  {
    path: '/staff/v1/instructions',
    schemas: {
      listItem: InstructionListItemSchema,
      detail: InstructionDetailSchema,
      create: null as any,
      update: null as any,
    },
  }
);

export const instructionHooks = {
  ...createResourceHooks(instructionClient, ['instructions']),
  
  useSend: () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: ({ id, bankRef, sentAt }: { id: string; bankRef: string; sentAt: string }) => 
        fetcher.post<InstructionDetail>(`/staff/v1/instructions/${id}/send`, { bankRef, sentAt }),
      onSuccess: (_, variables) => {
        qc.invalidateQueries({ queryKey: ['instructions', 'list'] });
        qc.invalidateQueries({ queryKey: ['instructions', 'detail', variables.id] });
      },
    });
  },

  useCancel: () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (id: string) => fetcher.post<InstructionDetail>(`/staff/v1/instructions/${id}/cancel`),
      onSuccess: (_, id) => {
        qc.invalidateQueries({ queryKey: ['instructions', 'list'] });
        qc.invalidateQueries({ queryKey: ['instructions', 'detail', id] });
      },
    });
  },

  useRetry: () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (id: string) => fetcher.post<InstructionDetail>(`/staff/v1/instructions/${id}/retry`),
      onSuccess: (_, id) => {
        qc.invalidateQueries({ queryKey: ['instructions', 'list'] });
        qc.invalidateQueries({ queryKey: ['instructions', 'detail', id] });
      },
    });
  },

  useApproveInstruction: () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (id: string) => fetcher.post<InstructionDetail>(`/staff/v1/instructions/${id}/approve`),
      onSuccess: (_, id) => {
        qc.invalidateQueries({ queryKey: ['instructions', 'list'] });
        qc.invalidateQueries({ queryKey: ['instructions', 'detail', id] });
      },
    });
  }
};


// --- Reconciliation Imports ---

export const importClient = createResourceClient<ImportListItem, ImportDetail, any, any>(
  fetcherConfig,
  {
    path: '/staff/v1/reconciliation/imports',
    schemas: {
      listItem: ImportListItemSchema,
      detail: ImportDetailSchema,
      create: null as any,
      update: null as any,
    },
  }
);

export const importHooks = {
  ...createResourceHooks(importClient, ['reconciliation-imports']),

  useMatch: () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: ({ rowId, instructionId }: { importId: string; rowId: string; instructionId: string }) => 
        fetcher.post<{ matched: true }>(`/staff/v1/reconciliation/match`, { rowId, instructionId }),
      onSuccess: (_, variables) => {
        qc.invalidateQueries({ queryKey: ['reconciliation-imports', 'detail', variables.importId] });
        qc.invalidateQueries({ queryKey: ['reconciliation-unmatched'] });
      },
    });
  },

  useUnmatchedRows: () => {
    return useQuery({
      queryKey: ['reconciliation-unmatched'],
      queryFn: () => fetcher.get<{ data: UnmatchedRow[] }>('/staff/v1/reconciliation/unmatched'),
    });
  },

  /**
   * G-86 — closes a row with a reason. The API refuses an empty reason, so the payload never
   * ships without one; `importId` is carried purely to know which detail cache to invalidate.
   */
  useResolveRow: () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: ({ rowId, reason }: { importId: string; rowId: string; reason: string }) =>
        fetcher.post<{ id: string; status: string }>(
          `/staff/v1/reconciliation/rows/${rowId}/resolve`,
          { reason },
        ),
      onSuccess: (_, variables) => {
        qc.invalidateQueries({ queryKey: ['reconciliation-imports', 'detail', variables.importId] });
        qc.invalidateQueries({ queryKey: ['reconciliation-imports', 'list'] });
        qc.invalidateQueries({ queryKey: ['reconciliation-unmatched'] });
      },
    });
  },

  /**
   * G-87 — fixes a wrongly imported value through the API instead of a database edit, and
   * reopens a resolved row back into the unmatched queue.
   */
  useCorrectRow: () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: ({
        rowId,
        correction,
      }: {
        importId: string;
        rowId: string;
        correction: ReconciliationCorrectionPayload;
      }) =>
        fetcher.post<{ id: string; status: string }>(
          `/staff/v1/reconciliation/rows/${rowId}/correct`,
          correction,
        ),
      onSuccess: (_, variables) => {
        qc.invalidateQueries({ queryKey: ['reconciliation-imports', 'detail', variables.importId] });
        qc.invalidateQueries({ queryKey: ['reconciliation-imports', 'list'] });
        qc.invalidateQueries({ queryKey: ['reconciliation-unmatched'] });
      },
    });
  },
};
